import "./roadmap.css";
import { PRODUCT, WEB_ROUTES } from "./config";
import {
  CloudflareRoadmapRepository,
  type RoadmapColumn,
  type RoadmapItem,
  type RoadmapRepository,
  type RoadmapSnapshot,
} from "./roadmap";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const value = document.createElement(name);
  if (className) value.className = className;
  if (text !== undefined) value.textContent = text;
  return value;
}

function setMetaDescription(content: string): void {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.append(meta);
  }
  meta.content = content;
}

function setCanonical(href: string): void {
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = href;
}

function roadmapCard(item: RoadmapItem, column: RoadmapColumn): HTMLElement {
  const card = element("article", "roadmap-card");
  card.dataset.itemId = item.id;
  if (item.landedInBuild !== null) {
    card.append(element(
      "p",
      "roadmap-build-badge",
      `Landed in Build ${item.landedInBuild}`,
    ));
  } else if (column.id === "doing_now") {
    card.append(element("p", "roadmap-now-badge", "Actively in flight"));
  }
  card.append(
    element("h3", undefined, item.title),
    element("p", "roadmap-card-summary", item.summary),
  );
  return card;
}

function roadmapColumn(column: RoadmapColumn): HTMLElement {
  const section = element(
    "section",
    `roadmap-column stage-${column.id.replaceAll("_", "-")}`,
  );
  section.setAttribute("aria-labelledby", `roadmap-${column.id}`);
  const header = element("header", "roadmap-column-header");
  const count = element(
    "span",
    "roadmap-column-count",
    String(column.items.length),
  );
  count.setAttribute(
    "aria-label",
    `${column.items.length} ${column.items.length === 1 ? "item" : "items"}`,
  );
  const headingRow = element("div", "roadmap-column-title");
  const title = element("h2", undefined, column.title);
  title.id = `roadmap-${column.id}`;
  headingRow.append(title, count);
  header.append(
    headingRow,
    element("p", undefined, column.description),
  );
  section.append(header);
  const list = element("div", "roadmap-card-list");
  if (column.items.length === 0) {
    list.append(element(
      "p",
      "roadmap-empty",
      "Nothing is being promised in this column right now.",
    ));
  } else {
    column.items.forEach((item) => list.append(roadmapCard(item, column)));
  }
  section.append(list);
  return section;
}

export class RoadmapPage {
  private readonly repository: RoadmapRepository;

  constructor(
    private readonly root: HTMLElement,
    dependencies: { repository?: RoadmapRepository } = {},
  ) {
    this.repository = dependencies.repository
      ?? new CloudflareRoadmapRepository();
  }

  async start(): Promise<void> {
    document.documentElement.dataset.surface = "roadmap";
    document.title = `Roadmap · ${PRODUCT.name}`;
    setMetaDescription(
      "The small, curated Convoful product roadmap: someday maybe, do soon, doing now, and done.",
    );
    setCanonical(`${WEB_ROUTES.canonicalOrigin}${WEB_ROUTES.roadmap}`);
    this.renderLoading();
    try {
      this.render(await this.repository.load());
    } catch {
      this.renderUnavailable();
    }
  }

  private shell(): {
    main: HTMLElement;
    content: HTMLElement;
  } {
    const main = element("main", "roadmap-shell");
    main.id = "app-content";
    main.tabIndex = -1;
    const header = element("header", "roadmap-site-header");
    const brand = element("a", "roadmap-brand");
    brand.href = WEB_ROUTES.marketing;
    brand.setAttribute("aria-label", `${PRODUCT.name} home`);
    const mark = element(
      "span",
      "brand-mark brand-mark-small",
      PRODUCT.brandMark,
    );
    mark.setAttribute("aria-hidden", "true");
    brand.append(mark, element("span", undefined, PRODUCT.name));
    const nav = element("nav", "roadmap-nav");
    nav.setAttribute("aria-label", "Roadmap");
    const howBuilt = element("a", undefined, "How it works");
    howBuilt.href = WEB_ROUTES.howBuilt;
    const open = element("a", "roadmap-open-link", "Open private beta");
    open.href = WEB_ROUTES.app;
    nav.append(howBuilt, open);
    header.append(brand, nav);
    const content = element("div");
    main.append(header, content);
    return { main, content };
  }

  private renderLoading(): void {
    const { main, content } = this.shell();
    const state = element("section", "roadmap-state");
    state.setAttribute("aria-live", "polite");
    state.append(
      element("p", "eyebrow", "Product roadmap"),
      element("h1", undefined, "Opening the board…"),
      element(
        "p",
        undefined,
        "Convoful is loading the current curated product story.",
      ),
    );
    content.append(state);
    this.root.replaceChildren(main);
  }

  private renderUnavailable(): void {
    const { main, content } = this.shell();
    const state = element("section", "roadmap-state");
    state.append(
      element("p", "eyebrow", "Product roadmap"),
      element("h1", undefined, "The roadmap is temporarily unavailable."),
      element(
        "p",
        undefined,
        "Convoful did not receive a complete, trustworthy roadmap response.",
      ),
    );
    const retry = element("button", "primary-button", "Try again");
    retry.type = "button";
    retry.addEventListener("click", () => void this.start());
    state.append(retry);
    content.append(state);
    this.root.replaceChildren(main);
  }

  private render(snapshot: RoadmapSnapshot): void {
    const { main, content } = this.shell();
    const intro = element("section", "roadmap-intro");
    const copy = element("div");
    copy.append(
      element("p", "eyebrow", "A small, honest product roadmap"),
      element("h1", undefined, "What Convoful is becoming."),
      element(
        "p",
        "roadmap-lede",
        "What we might explore, what we want to do soon, what is genuinely underway, and what you can already use.",
      ),
    );
    const status = element("aside", "roadmap-current-build");
    status.setAttribute("aria-label", "Current iPhone build");
    status.append(
      element("span", undefined, "Current iPhone build"),
      element("strong", undefined, String(snapshot.currentBuild)),
      element(
        "p",
        undefined,
        "Every Done card is present here. Its badge shows the first build that included it.",
      ),
    );
    intro.append(copy, status);

    const context = element("section", "roadmap-context");
    context.append(
      element(
        "p",
        undefined,
        "GitHub tracks the engineering work. This page deliberately tells a smaller, human product story.",
      ),
      element(
        "p",
        undefined,
        "Doing now means active and likely for the next build—not a promise or release date.",
      ),
    );

    const board = element("div", "roadmap-board");
    snapshot.columns.forEach((column) => board.append(roadmapColumn(column)));

    const footer = element("footer", "roadmap-footer");
    footer.append(
      element(
        "span",
        undefined,
        "Engineering details stay in the private project tracker.",
      ),
      element(
        "span",
        undefined,
        `Curated ${new Date(snapshot.updatedAt).toLocaleDateString(
          "en-US",
          { month: "long", day: "numeric", year: "numeric" },
        )}`,
      ),
    );
    content.append(intro, context, board, footer);
    this.root.replaceChildren(main);
  }
}

export async function renderRoadmap(root: HTMLElement): Promise<void> {
  await new RoadmapPage(root).start();
}
